import { useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import { FormStaticSelect } from '../../../../../components/tables/filterForms/FormStaticSelect';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import { useGetAllPagesQuery } from '../../../../../services/endpoints/website/page';
import { getCurrentUser } from '../../../../../services/helper';
import { Page } from '../../../../../types/website.types';
import { pageColumns } from '../../../../../utils/consts/columnDefs/websiteColumns';
import { getCreatedUpdatedAtFromTo } from '../../../../../utils/consts/filterFields/dates';
import { getImageFilterFields } from '../../../../../utils/consts/filterFields/image';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';
import { canEditPages } from '../../../../../utils/rbac';

const defaultScope = '';
const currentUser = getCurrentUser();

export const PagesView = () => {
  const { addToast } = useToast();
  const [filters, setFilters] = useListFiltersInUrl({
    scope: defaultScope,
    data: { page: 1, entries: 10 }
  });
  const { data, error, isLoading, isFetching } = useGetAllPagesQuery(filters);
  const [pages, setPages] = useState<Page[]>([]);
  const navigate: NavigateFunction = useNavigate();
  const isGlobalUser = currentUser?.isGlobal;

  const filterFormFields: FilterField[] = [
    // TODO: add Slugs and Gallery filters - not working well on Dash 2.0
    {
      name: 'name',
      label: 'Name',
      value: getFilterValue(filters.data?.filterBy, 'name')
    },
    {
      name: 'content',
      label: 'Content',
      value: getFilterValue(filters.data?.filterBy, 'content')
    },
    {
      name: 'slug',
      label: 'Slug',
      value: getFilterValue(filters.data?.filterBy, 'slug')
    },
    {
      name: 'is_featured',
      type: 'number',
      operator: 'equals',
      element: (
        <FormStaticSelect
          name="is_featured"
          label="Is Featured"
          options={[
            { value: 1, label: 'Yes' },
            { value: 0, label: 'No' }
          ]}
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'is_featured', 'equals')
    },
    {
      name: 'author',
      label: 'Author',
      value: getFilterValue(filters.data?.filterBy, 'author')
    },
    {
      name: 'button_text',
      label: 'Button Text',
      value: getFilterValue(filters.data?.filterBy, 'button_text')
    },
    {
      name: 'button_target',
      label: 'Button Target',
      value: getFilterValue(filters.data?.filterBy, 'button_target')
    },
    {
      name: 'button_opens_in_new_window',
      type: 'number',
      operator: 'equals',
      element: (
        <FormStaticSelect
          name="button_opens_in_new_window"
          label="Button Opens In New Window"
          options={[
            { value: 1, label: 'Yes' },
            { value: 0, label: 'No' }
          ]}
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'button_opens_in_new_window', 'equals')
    },
    ...getImageFilterFields(filters.data?.filterBy),
    {
      name: 'published_on_from',
      id: 'published_on',
      operator: 'greater_equal_than',
      element: (
        <DateFilterField
          name="published_on_from"
          label="Published On"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'published_on', 'greater_equal_than')
    },
    {
      name: 'published_on_to',
      id: 'published_on',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="published_on_to" />,
      value: getFilterValue(filters.data?.filterBy, 'published_on', 'lesser_equal_than')
    },
    ...getCreatedUpdatedAtFromTo(filters.data?.filterBy)
  ];

  const visibleFilterFields = isGlobalUser
    ? filterFormFields
    : filterFormFields.filter((f) => f.name !== 'is_featured');

  useEffect(() => {
    if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setPages(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  return (
    <div className="py-2">
      <SFCTable
        name={`Pages`}
        columns={pageColumns}
        data={pages}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        isLoading={isLoading}
        isFetching={isFetching}
        indexDownloadPath="page"
        filters={filters?.data?.filterBy}
        filterForm={
          <FilterForm
            fields={visibleFilterFields}
            onApplyFilters={setFilterBy}
          />
        }
        newItemEl={
          canEditPages(currentUser) && (
            <SimpleButton
              variant="sfc-blueberry"
              height={40}
              truncate={false}
              onClick={() => navigate('/admin/website/pages/new')}
            >
              New Page
            </SimpleButton>
          )
        }
        onFiltersChanged={setFilters}
      />
    </div>
  );
};
